import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { NotificationService } from './notification.service';

export interface LocationPing {
  sessionId: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
}

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  private options: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 20000
  };
  
  private lastPosition: GeolocationPosition | null = null;
  private locationErrorLogged = false;
  private locationTrackingActive = false;
  private locationTrackingInterval: any = null;

  constructor(
    private http: HttpClient,
    private notify: NotificationService
  ) {}

  /**
   * Check if geolocation is supported
   */
  isGeolocationSupported(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Request location permission and get current position
   */
  async requestLocationPermission(): Promise<GeolocationPosition | null> {
    if (!this.isGeolocationSupported()) {
      this.notify.showToast('error', 'GoVideo', 'Geolocation is not supported by your browser');
      return null;
    }

    try {
      const position = await this.getCurrentPosition();
      this.lastPosition = position;
      this.notify.showToast('success', 'GoVideo', 'Location permission granted');
      return position;
    } catch (error: any) {
      this.notify.showToast('error', 'GoVideo', `Location permission denied: ${error.message}`);
      return null;
    }
  }

  /**
   * Get current position
   */
  private getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.lastPosition = position;
          resolve(position);
        },
        (error: GeolocationPositionError) => {
          reject(error);
        },
        this.options
      );
    });
  }

  /**
   * Get cached position
   */
  getCachedPosition(): GeolocationPosition | null {
    return this.lastPosition;
  }

  /**
   * Upload location to server
   */
  private async uploadLocation(sessionId: string, coords: GeolocationCoordinates): Promise<void> {
    try {
      // Skip if coordinates are invalid (0,0 or null island)
      if (Math.abs(coords.latitude) < 0.1 && Math.abs(coords.longitude) < 0.1) {
        console.warn('Skipping invalid coordinates:', coords);
        return;
      }

      const locationPing: LocationPing = {
        sessionId,
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy
      };

      await this.http.post(`${environment.apiUrl}/location`, locationPing).toPromise();
      console.log('Location uploaded:', locationPing);
    } catch (error) {
      console.error('Failed to upload location:', error);
    }
  }

  /**
   * Start tracking location every 30 seconds
   */
  async startLocationTracking(sessionId: string): Promise<void> {
    if (this.locationTrackingActive) {
      console.warn('Location tracking already active');
      return;
    }

    if (!this.isGeolocationSupported()) {
      this.notify.showToast('error', 'GoVideo', 'Geolocation not supported');
      return;
    }

    this.locationTrackingActive = true;
    
    // Get initial position
    try {
      const position = await this.getCurrentPosition();
      await this.uploadLocation(sessionId, position.coords);
    } catch (error: any) {
      console.error('Error getting initial location:', error);
      if (!this.locationErrorLogged) {
        this.notify.showToast('error', 'GoVideo', `Location error: ${error.message}`);
        this.locationErrorLogged = true;
      }
    }

    // Track location every 30 seconds
    this.locationTrackingInterval = setInterval(async () => {
      if (!this.locationTrackingActive) {
        return;
      }

      try {
        const position = await this.getCurrentPosition();
        await this.uploadLocation(sessionId, position.coords);
      } catch (error: any) {
        console.error('Error tracking location:', error);
        if (!this.locationErrorLogged) {
          this.notify.showToast('error', 'GoVideo', `Location tracking error: ${error.message}`);
          this.locationErrorLogged = true;
        }
      }
    }, 30000); // 30 seconds

    console.log('Location tracking started for session:', sessionId);
  }

  /**
   * Stop location tracking
   */
  stopLocationTracking(): void {
    this.locationTrackingActive = false;
    if (this.locationTrackingInterval) {
      clearInterval(this.locationTrackingInterval);
      this.locationTrackingInterval = null;
    }
    console.log('Location tracking stopped');
  }

  /**
   * Get all location pings for a session
   */
  getLocationPings(sessionId: string): Promise<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/location/${sessionId}`).toPromise() as Promise<any[]>;
  }

  /**
   * Get the most recent location ping for a session
   */
  getRecentLocationPing(sessionId: string): Promise<any> {
    return this.http.get<any>(`${environment.apiUrl}/location/${sessionId}/recent`).toPromise();
  }

  /**
   * Get map image data URL for a session
   * @param sessionId - The session ID
   * @param mode - Display mode: 'path' (all points with lines) or 'latest' (most recent only)
   * @param width - Image width in pixels (default 640)
   * @param height - Image height in pixels (default 640)
   * @returns Base64 data URL of the map image
   */
  async getMapImage(
    sessionId: string,
    mode: 'path' | 'latest' = 'latest',
    width: number = 640,
    height: number = 640
  ): Promise<string> {
    const response = await this.http.get<{ url: string }>(
      `${environment.apiUrl}/location/${sessionId}/map-image?width=${width}&height=${height}&mode=${mode}`
    ).toPromise();
    return response?.url || '';
  }

  /**
   * Get Google Maps URL for a session
   * @param sessionId - The session ID
   * @returns Google Maps URL for the location
   */
  async getMapUrl(sessionId: string): Promise<string> {
    const response = await this.http.get<{ url: string }>(
      `${environment.apiUrl}/location/${sessionId}/map-url`
    ).toPromise();
    return response?.url || '';
  }
}

